import React, { useState } from "react";
import PropTypes from "prop-types";
import { useController, useFormContext } from "react-hook-form";

import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
//import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";

import { getFileSizeLabel } from "../../utils/getFileSizeLabel";
import { useHubSnackBar } from "../../hooks";
import { HubButton, InputLabel } from "../";

// eslint-disable-next-line
import { StyledDragnDrop, StyledProgressStack } from "./styles";
import { Divider } from "@mui/material";

const MAX_FILES = 5;
const MAX_SIZE_MEGABYTES = 500;

export const HubHookFormFileInput = ({
  name = "fileInput",
  limit = MAX_FILES,
  sizeLimit = MAX_SIZE_MEGABYTES,
  isLoading = false,
  label = "File Upload",
  required = false,
  shouldUnregister = true,
}) => {
  const { addSnackbarError } = useHubSnackBar();

  const [hovering, setHovering] = useState(false); // eslint-disable-next-line
  const [uploadProgress, setUploadProgress] = useState(0); // eslint-disable-next-line
  const [size, setSize] = useState("0 KB"); // eslint-disable-next-line
  const [files, setFiles] = useState([]);

  const [uploadedFileCount, setUploadedFileCount] = useState(0);

  const { getValues } = useFormContext();

  const {
    field: { onChange, ref, ...inputProps },
  } = useController({
    name: name,
    rules: { required },
    shouldUnregister,
  });

  const handleFileUpload = (event) => {
    const files = Object.values(event.target.files);
    handleFileReading(files);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    setHovering(true);
  };

  const handleDrop = (event) => {
    setHovering(false);
    event.preventDefault();
    if (isLoading) {
      return addSnackbarError({
        message:
          "Please wait for the previous upload to finish before including another file.",
      });
    }
    const files = Object.values(event.dataTransfer.files);
    handleFileReading(files);
  };

  const calculateFileSize = (files) => {
    const currentSize = files[0]?.size;

    const fileInputState = getValues(name);

    const sum = fileInputState?.length
      ? fileInputState?.reduce(
          (total, file) => total + (file?.uploadedFileSize || 0),
          0,
        ) + currentSize
      : currentSize;

    const sizeLabel = getFileSizeLabel(sum);
    setSize(sizeLabel);
    return sum;
  };

  const handleFileReading = (files = []) => {
    try {
      if (
        files.length > limit ||
        getValues(name)?.length + files.length > limit
      ) {
        return addSnackbarError({
          message: `You can only upload up to ${limit} files.`,
        });
      }

      const newTotalFileSize = calculateFileSize(files);

      if (newTotalFileSize > sizeLimit * 1000000) {
        return addSnackbarError({
          message: `You can only upload up to ${sizeLimit} MB of files.`,
        });
      }

      setUploadedFileCount(uploadedFileCount + 1);
      setUploadProgress(0);

      files?.forEach(async (file, index) => {
        const isVideo = file.type.startsWith("video/");

        if (isVideo) {
        } else {
        }

        setUploadProgress(((index + 1) / files.length) * 100);
      });
    } catch (e) {
      //remove all previous files
      onChange([]);
      setFiles([]);

      // logging error
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  return (
    <Stack width="100%">
      <InputLabel label={label} required={required} />
      <StyledDragnDrop
        onDragOver={handleDragOver}
        onDragLeave={() => setHovering(false)}
        onDrop={handleDrop}
        hovering={hovering}
      >
        <input
          type="file"
          id="Content_Drag_Drop_Input_Field"
          accept="image/*, video/*, application/pdf, text/plain, text/html, application/msword, application/vnd.ms-excel, application/vnd.ms-powerpoint"
          multiple
          style={{ display: "none" }}
          onChange={handleFileUpload}
        />
        <Stack gap="5px" alignItems="center">
          <Typography variant="greyDark12Bold" align="center">
            Drag and Drop
          </Typography>
          <HubButton
            variant="text"
            size="xsmall"
            component="label"
            htmlFor="Content_Drag_Drop_Input_Field"
            icon="Share"
            iconProps={{ size: "10px" }}
            label="Browse Device"
            disabled={isLoading || hovering} //add file limit check
            removePreventDefault
          />
        </Stack>
        {isLoading && (
          <Box sx={{ display: "flex" }}>
            <CircularProgress />
          </Box>
        )}
        <input
          {...inputProps}
          onChange={setFiles}
          ref={ref}
          style={{ display: "none" }}
          disabled={isLoading}
        />
      </StyledDragnDrop>
      {/* <StyledProgressStack>
        <Stack>
          <HubIcon icon="Video" />
          <Typography variant="darkBold14">{`Upload Progress: ${uploadProgress.toFixed(
            0
          )}%`}</Typography>
        </Stack>
        <Typography variant="caption">{`${size} - ${uploadedFileCount} files uploaded.`}</Typography>
        <LinearProgress variant="determinate" value={uploadProgress} />
      </StyledProgressStack> */}
    </Stack>
  );
};

HubHookFormFileInput.propTypes = {
  defaultValue: PropTypes.string,
  name: PropTypes.string,
  limit: PropTypes.number,
  sizeLimit: PropTypes.number,
  setCanSubmitForm: PropTypes.func,
  setFiles: PropTypes.func,
  files: PropTypes.arrayOf(PropTypes.objkect),
};

export default HubHookFormFileInput;
